import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ProfileForm } from "../components/dashboard/ProfileForm";
import { PhotosManager } from "../components/dashboard/PhotosManager";
import { Inbox } from "../components/dashboard/Inbox";
import { Membership } from "../components/dashboard/Membership";
import { CastingCalls } from "../components/dashboard/CastingCalls";
import { formatDate } from "../lib/api";
import { ShareButtons } from "../components/ShareButtons";
import { WebAddress } from "../components/dashboard/WebAddress";

const StatusBar = ({ profile }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 border border-white/10 mt-10" data-testid="dashboard-status-bar">
    <div className="p-6 sm:border-r border-white/10">
      <p className="label-xs">Listing status</p>
      <p className={`font-display text-2xl mt-2 ${profile.active ? "text-primary" : "text-white/70"}`} data-testid="dashboard-listing-status">
        {profile.active ? "Live" : profile.hidden ? "Hidden by admin" : "Not live"}
      </p>
    </div>
    <div className="p-6 sm:border-r border-white/10">
      <p className="label-xs">{profile.active && profile.renews_on ? (profile.cancel_at_period_end ? "Ends on" : profile.subscription_status === "trialing" ? "Free until" : "Renews on") : profile.active ? "Live until" : "Expiry"}</p>
      <p className="font-display text-2xl mt-2" data-testid="dashboard-paid-until">{profile.renews_on && profile.active ? formatDate(profile.renews_on) : profile.paid_until ? formatDate(profile.paid_until) : "—"}</p>
    </div>
    <div className="p-6">
      <p className="label-xs">Photos</p>
      <p className="font-display text-2xl mt-2" data-testid="dashboard-photo-count">{profile.photos.length} / 8</p>
    </div>
  </div>
);

export default function Dashboard() {
  const [profile, setProfile] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "profile");

  useEffect(() => {
    api.get("/profile/me").then((r) => setProfile(r.data));
  }, []);

  if (!profile) return <div className="min-h-[60vh] grid place-items-center label-xs">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="label-xs">Your dashboard</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3">{profile.display_name || "Your profile"}</h1>
        </div>
        {profile.active && (
          <Link to={`/${profile.slug}`} className="inline-flex items-center gap-2 text-primary text-sm font-bold uppercase tracking-wider" data-testid="dashboard-view-public-link">
            View public profile <ExternalLink size={14} />
          </Link>
        )}
      </div>
      <StatusBar profile={profile} />
      <WebAddress profile={profile} onChange={(slug) => setProfile({ ...profile, slug, slug_needs_choice: false })} />
      {profile.active && (
        <div className="border border-white/10 border-t-0 px-6 pb-6" data-testid="dashboard-share-panel">
          <ShareButtons slug={profile.slug} name={profile.display_name} title="Promote your profile" />
        </div>
      )}
      {!profile.active && !profile.hidden && (
        <button onClick={() => setTab("membership")} className="w-full mt-4 text-left border border-primary/40 bg-primary/5 p-4 text-sm hover:bg-primary/10 transition-colors" data-testid="dashboard-go-live-banner">
          {profile.paid_until ? "Your subscription has ended, so your profile isn't visible in search." : "Your profile isn't visible in search yet."}{" "}
          <span className="text-primary font-bold">{profile.stripe_subscription_id ? "Subscribe for £10 / $14 / €12 a year to go live →" : "Start your 6 months free to go live →"}</span>
        </button>
      )}
      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start h-auto p-0 gap-6 overflow-x-auto">
          {[["profile", "Profile"], ["photos", "Photos"], ["casting", "Casting calls"], ["messages", "Messages"], ["membership", "Membership"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-none px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary uppercase tracking-wider text-xs font-bold" data-testid={`dashboard-tab-${v}`}>
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="profile" className="mt-10"><ProfileForm profile={profile} onSaved={setProfile} /></TabsContent>
        <TabsContent value="photos" className="mt-10"><PhotosManager profile={profile} onChange={setProfile} /></TabsContent>
        <TabsContent value="casting" className="mt-10"><CastingCalls profile={profile} /></TabsContent>
        <TabsContent value="messages" className="mt-10"><Inbox /></TabsContent>
        <TabsContent value="membership" className="mt-10"><Membership profile={profile} onChange={setProfile} /></TabsContent>
      </Tabs>
    </div>
  );
}
